import type { ReactNode } from 'react';
import { AirplaneTakeoffIcon, BuildingsIcon, BusIcon, CarIcon } from '@phosphor-icons/react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { unhandledCase } from './unhandled_case.js';

/** Uma linha do pedido no painel "Sua viagem": a ida já escolhida (guardada, não
 *  emitida) ou o item pedido à equipe (tracejado, "a cotar" no lugar do valor). */
export interface RequestServiceRowItem {
  key: string;
  icon: 'flight' | 'hotel' | 'bus' | 'rental';
  title: string;
  subtitle?: string;
  /** O valor, ou "a cotar". */
  trailing?: string;
  badge: { label: string; tone: 'neutral' | 'warning' };
  /** O item ainda pedido (tracejado), não o já escolhido. */
  dashed?: boolean;
}

function RowIcon({ icon }: { readonly icon: RequestServiceRowItem['icon'] }): ReactNode {
  switch (icon) {
    case 'flight':
      return <AirplaneTakeoffIcon size={16} aria-hidden />;
    case 'hotel':
      return <BuildingsIcon size={16} aria-hidden />;
    case 'bus':
      return <BusIcon size={16} aria-hidden />;
    case 'rental':
      return <CarIcon size={16} aria-hidden />;
    default:
      return unhandledCase(icon);
  }
}

/** Uma linha de serviço com a calha do ícone à esquerda — a anatomia do itinerário do
 *  painel de viagem. `min-w-0` na `li` e na linha do título: sem isso as trilhas dos
 *  grids crescem até a largura do texto e o cartão estoura a coluna lateral. */
export function RequestServiceRow({ row }: { readonly row: RequestServiceRowItem }): ReactNode {
  return (
    <li className="flex min-w-0 gap-2">
      <span className="text-muted-foreground after:bg-border flex w-6 shrink-0 flex-col items-center gap-1 pt-2 after:w-px after:flex-1">
        <RowIcon icon={row.icon} />
      </span>
      <div
        className={`grid min-w-0 flex-1 gap-0.5 rounded-lg p-3 ${
          row.dashed
            ? 'border-border bg-muted border border-dashed'
            : 'border-border bg-card border shadow-xs'
        }`}
      >
        <div className="flex min-w-0 items-start gap-2">
          {/* Duas linhas, não reticências: o título do pedido carrega a rota inteira
              ("GRU → REC · REC → GRU · pedido à equipe") e truncado perdia o que o
              diferencia da ida guardada. */}
          <span className="text-ui line-clamp-2 min-w-0 flex-1 font-semibold">{row.title}</span>
          {row.trailing && (
            <span className="text-caption text-muted-foreground font-semibold whitespace-nowrap">
              {row.trailing}
            </span>
          )}
        </div>
        {row.subtitle && <span className="text-caption text-muted-foreground">{row.subtitle}</span>}
        <span className="mt-1 flex">
          <Badge variant="soft" color={row.badge.tone}>
            {row.badge.label}
          </Badge>
        </span>
      </div>
    </li>
  );
}

/** O que o pedido põe na viagem, como lista pronta. As duas telas do pedido a
 *  desenham: o checkout (antes do envio) e a tela "Solicitação enviada" do host
 *  (depois) — o mesmo item, nos dois momentos. */
export function RequestServiceRows({
  rows,
}: {
  readonly rows: readonly RequestServiceRowItem[];
}): ReactNode {
  return (
    <ul className="m-0 grid min-w-0 list-none gap-2 p-0">
      {rows.map((row) => (
        <RequestServiceRow key={row.key} row={row} />
      ))}
    </ul>
  );
}
